import "./ActionIcons.less";

import { GuxIcon, GuxTooltip } from "genesys-spark-components-react";
import strings from "./ActionIcons.strings";
import { useStrings } from "features/i18n/useStrings";
import { useEffect, useRef, useState } from "react";
import { uniqueId } from "lodash";
import { HelpButton } from "./HelpButton";
import { GuxIconIconName } from "genesys-spark-components";

export function ActionIcon({
    iconName,
    text,
    performAction,
    className,
}: {
    iconName: GuxIconIconName;
    text: string;
    performAction?: () => void;
    className?: string;
}) {
    const randomId = uniqueId();
    const [showToolTip, setShowToolTip] = useState(false);
    const ref = useRef<HTMLGuxIconElement>(null);
    const classes =
        className !== undefined
            ? className
            : `ActionIcon Clickable ${performAction ? "" : " disabled"}`;

    useEffect(() => {
        if (showToolTip) {
            ref.current?.dispatchEvent(new Event("focusin"));
        }
    }, [showToolTip]);
    return (
        <div
            className={classes}
            onPointerEnter={() => {
                setShowToolTip(true);
            }}
            onPointerLeave={() => setShowToolTip(false)}
            onFocus={() => setShowToolTip(true)}
            onBlur={() => setShowToolTip(false)}
            aria-disabled={performAction === undefined}
        >
            <GuxIcon
                data-testid={`${iconName}-icon-button`}
                role="button"
                iconName={iconName}
                onClick={performAction}
                onKeyDown={(event) => {
                    if (performAction && event.key === "Enter") {
                        performAction();
                    }
                }}
                tabIndex={performAction ? 0 : -1}
                id={randomId}
                ref={ref}
                screenreaderText={iconName}
            ></GuxIcon>
            {showToolTip && <GuxTooltip for={randomId}>{text}</GuxTooltip>}
        </div>
    );
}

export function DeleteButton({ onDelete }: { onDelete?: () => void }) {
    const t = useStrings(strings);
    return (
        <ActionIcon
            iconName="delete"
            text={t.delete}
            performAction={onDelete}
        />
    );
}

export function EditButton({ onEdit }: { onEdit: () => void }) {
    const t = useStrings(strings);
    return <ActionIcon iconName="edit" text={t.edit} performAction={onEdit} />;
}

export function ActionButtons({
    helpText = undefined,
    onEdit,
    onDelete,
}: {
    helpText?: string;
    onEdit: () => void;
    onDelete?: () => void;
}) {
    return (
        <div className="ActionButtons">
            <HelpButton helpText={helpText} />
            <EditButton onEdit={onEdit} />
            <DeleteButton onDelete={onDelete} />
        </div>
    );
}

export function ReorderButtons({
    onMoveUp,
    onMoveDown,
}: {
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}) {
    const t = useStrings(strings);
    return (
        <div className="ReorderButtons" data-testid="reorder-buttons">
            <ActionIcon
                iconName="arrow-up"
                text={t.up}
                performAction={onMoveUp}
            />
            <ActionIcon
                iconName="arrow-down"
                text={t.down}
                performAction={onMoveDown}
            />
        </div>
    );
}
